import { Resolution, PhysicsConstants, RankPerTier } from "./constants";
import { mat2, vec2 } from "@tlaukkan/tsm";
import { World, SystemConstructor, System, Entity, ComponentConstructor, Component } from "ecsy";
import { ArrayVec2 } from "./synchronization";
import { ShopConstants } from "./constants";
import { Chained, ColliderShape, IsBeingDragged as Dragging, MergableComponent } from "./components";
import { GameElement } from "./gameElementTypes";
import { Rng } from "./rng";
import { Game } from "./game";
import { ImmuneCellClass, RewardCellClass } from "./immuneCellTiers";

//Implement seperately client vs server
declare global {
	var getStepNumber: () => number;
}

export type Callback<T> = (t: T) => void;

//TODO: this is a hack to allow late init by ecsy and other lateinit workarounds. Figure out a nicer way.
export const Uninitialized = undefined as any;

export interface SimpleTransform {
	position: vec2,
	size: vec2,
	rotation: number,
	shape: ColliderShape
}

export function degToRad(degrees: number) {
	return degrees * (Math.PI / 180);
}

export function radToDeg(radians: number) {
	return radians * (180 / Math.PI);
}

export function metersToPixels(m: number) {
	return m * PhysicsConstants.MetersToPixelsConversion;
}

export function pixelsToMeters(p: number) {
	return p * PhysicsConstants.PixelsToMetersConversion;
}

const OffsetToCenterPixels = new vec2([Resolution.Width / 2, -Resolution.Height / 2]);

export type AssetLocation = {
	x: number,
	y: number,
	width: number,
	height: number
}

export function worldToScreenPosition(worldPosition: vec2, dest: vec2 = new vec2()) {
	dest.xy = [
		metersToPixels(worldPosition.x),
		metersToPixels(-worldPosition.y) + Resolution.Height
	];
	dest.add(OffsetToCenterPixels);
	return dest;
}

export function screenToWorldPosition(screenPosition: vec2, dest = new vec2()) {
	screenPosition.subtract(OffsetToCenterPixels);
	dest.xy = [
		pixelsToMeters(screenPosition.x),
		pixelsToMeters(-screenPosition.y + Resolution.Height)
	];
	return dest;
}

export function printObjectProperties(obj: Object) {
	for (const key in obj) {
		if (!key.includes("emscripten")) {
			console.log(key);
		}
	}
}

export function assertServer() {
	if (!process.env.is_server)
		throw new Error('assertServer failed. Are you including a server only file, or running a function intended for server only use?')
}

export function assertClient() {
	if (process.env.is_server)
		throw new Error('assertClient failed. Are you including a client only file, or running a function intended for client only use?')
}

export function vecToStr(v: { x: number, y: number }) {
	return `(${v.x.toFixed(2)}, ${v.y.toFixed(2)})`
}

export function equalsArrayVec2(a: ArrayVec2, b: ArrayVec2) {
	return a[0] === b[0] && a[1] === b[1]
}

//Ecsy utils
export function getSystem<T extends System>(world: World, system: SystemConstructor<T>): T {
	return world.getSystem(system) as T;
}

/** A system the client keeps running while the game is paused. Only this runs then, not execute, once a frame.
 * It must not add or remove components that other systems query: they don't run while paused, so they'd get a backlog
 * of events to handle all at once on resume */
export interface ExecutesEvenIfPaused {
	executeEvenIfPaused(delta: number, time: number): void;
}

export function executesEvenIfPaused(system: object): system is ExecutesEvenIfPaused {
	return typeof (system as Partial<ExecutesEvenIfPaused>).executeEvenIfPaused === 'function';
}

export type FrozenComponent = {
	component: new () => Component,
	values: Object,
	needsDeepCopy: boolean
};

export function freezeComponent<T extends Component>(
	component: ComponentConstructor<T>,
	values: Partial<T> = {},
	options = { needsDeepCopy: false }
): FrozenComponent {
	return { component, values, needsDeepCopy: options.needsDeepCopy };
}

export function addComponent<T extends Component>(
	entity: Entity,
	component: ComponentConstructor<T>,
	values?: Partial<T>
) {
	if (entity.hasComponent(component)) {
		const prev = entity.getComponent(component) as unknown as MergableComponent<T>;
		if (prev.merge && values) {
			prev.merge(values);
			return entity.getMutableComponent(component); //merging should count as mutation
		}
		return entity.getComponent(component)
	}
	else {
		entity.addComponent(component, values);
		return entity.getMutableComponent(component);
	}
}

export function getOrAddComponent<T extends Component>(
	entity: Entity,
	component: ComponentConstructor<T>,
	values?: Partial<T>
) {
	const comp = entity.getComponent(component);
	if (comp) return comp;
	return addComponent(entity, component, values);
}

export function removeComponentIfPresent<T extends Component>(
	entity: Entity,
	component: ComponentConstructor<T>,
) {
	if (entity.hasComponent(component)) {
		entity.removeComponent(component);
	}
}

export function clamp(input: number, min: number, max: number) {
	if (input < min) return min;
	if (input > max) return max;
	else return input;
}

export function angleBetween(a: vec2, b: vec2) {
	a = a.copy().normalize();
	b = b.copy().normalize();
	const angle = Math.atan2(b.y, b.x) - Math.atan2(a.y, a.x);
	if (angle > Math.PI) return angle - 2 * Math.PI;
	else if (angle <= - Math.PI) return angle + 2 * Math.PI;
	return angle;
}

export function lineFrom(from: vec2, to: vec2) {
	return to.copy().subtract(from).normalize();
}


const up = new vec2([0, 1]);
export function angleFromUp(point: vec2) {
	return angleBetween(up, point);
}

export function rotateAround(point: vec2, radians: number, around: vec2, dest: vec2 = new vec2()) {
	const rotate = new mat2()
	vec2.difference(point, around, dest);
	rotate.setIdentity().rotate(radians);
	dest.multiplyMat2(rotate);
	return dest.add(around);
}

export function normalizeAngleRadiens(angle: number) {
	angle = angle % (2 * Math.PI);
	if (angle > Math.PI) {
		angle -= 2 * Math.PI;
	} else if (angle < -Math.PI) {
		angle += 2 * Math.PI;
	}
	return angle;
}

// Function to adjust angle difference
export function smallestAngleBetween(from: number, to: number) {
	let difference = normalizeAngleRadiens(to) - normalizeAngleRadiens(from);
	if (difference > Math.PI) {
		difference -= 2 * Math.PI;
	} else if (difference < -Math.PI) {
		difference += 2 * Math.PI;
	}
	return difference;
}

export function seededRandom(seed: number) {
	const sin = Math.sin(seed);
	return sin - Math.floor(sin);
}

export function uniformDistrubtion(length: number, rng?: Rng) {
	const chance = 1 / length;
	const rand = rng === undefined ? Math.random() : rng.next();
	for (let i = 0; i < length; ++i) {
		const lowerBound = chance * i;
		const upperBound = chance * (i + 1);
		if (rand > lowerBound && rand <= upperBound) {
			return i;
		}
	}
	return length - 1;
}

export function shuffleArray(array: number[], rng?: Rng): number[] {
	const shuffledArray: number[] = [...array];

	for (let i = shuffledArray.length - 1; i > 0; i--) {
		const rand = rng === undefined ? Math.random() : rng.next();
		const randomIndex = Math.floor(rand * (i + 1));
		[shuffledArray[i], shuffledArray[randomIndex]] = [shuffledArray[randomIndex], shuffledArray[i]];
	}

	return shuffledArray;
}

export function calculateRoundShopTime(game: Game) {
	return Infinity;
	//Timer disabled even in multiplayer, for now

	// const isSinglePlayer = game.players.length === 1
	// if (isSinglePlayer) return Infinity;

	// const round = game.getCurrentRound();
	// const additionalTime = round * ShopConstants.RoundIncreaseTimer;
	// return Math.max(additionalTime + ShopConstants.ShopDurationMS);
}

//From: https://gist.github.com/nikolas/b0cce2261f1382159b507dd492e1ceef
export function lerpColor(a: number, b: number, progress: number) {
	const ar = a >> 16,
		ag = a >> 8 & 0xff,
		ab = a & 0xff,

		br = b >> 16,
		bg = b >> 8 & 0xff,
		bb = b & 0xff,

		rr = ar + progress * (br - ar),
		rg = ag + progress * (bg - ag),
		rb = ab + progress * (bb - ab);

	return (rr << 16) + (rg << 8) + (rb | 0);
};

//from chatgpt
export function brighten(color: number, progress: number): number {
	// Ensure progress is within the range [0, 1]
	progress = Math.max(0, Math.min(1, progress));

	// Extract the RGB components
	let r = (color >> 16) & 0xff;
	let g = (color >> 8) & 0xff;
	let b = color & 0xff;

	// Brighten each component
	const brightenValue = (value: number): number => {
		return Math.round(value + (255 - value) * progress);
	};

	r = brightenValue(r);
	g = brightenValue(g);
	b = brightenValue(b);

	// Reconstruct the color and return it
	return (r << 16) | (g << 8) | b;
}


export function perpendicular(vec: vec2, dest?: vec2) {
	if (!dest) dest = new vec2();
	dest.xy = [-vec.y, vec.x];
	return dest;
};

export function uniformVec2(x: number) {
	return new vec2([x, x]);
}

/***(a, b) -> unique C, given a and b < 1000 && > -1000 */
export function pairHash(a: number, b: number, limit = 1000) {
	a = a + limit;
	b = b + limit
	return a * 1000 + b;
}

if (!process.env.is_server) {
	(window as any) = {};
	(window as any).vec2 = vec2;
	(window as any).angleBetween = angleBetween;
	(window as any).angleFromUp = angleFromUp;
}

export function getImmuneCellTier(immuneCell: GameElement) {
	return (ImmuneCellClass.findIndex(tier => tier.some(antibody => antibody === immuneCell)) + 1);
}

export function getRewardCellTier(immuneCell: GameElement) {
	return (RewardCellClass.findIndex(tier => tier.some(antibody => antibody === immuneCell)) + 1);
}

export function getConnectedCells(entity: Entity, alreadyChecked: Set<Entity> = new Set(), excludeStarting = false): Entity[] {
	const openEdges = new Array<Entity>();
	openEdges.push(entity);
	while (openEdges.length > 0) {
		const entity = openEdges.pop()!;
		if (alreadyChecked.has(entity)) continue;
		const isDraggingOnClient = entity.hasComponent(Dragging);
		if (isDraggingOnClient) continue;

		alreadyChecked.add(entity);
		const chained = entity.getComponent(Chained);
		if (chained) {
			openEdges.push(...chained.links);
		}
	}
	if (excludeStarting) alreadyChecked.delete(entity);
	return Array.from(alreadyChecked);
}


export function deepCopy(obj: Object) {
	//TODO: When it works everywhere move this to structuredClone
	return JSON.parse(JSON.stringify(obj));
}

export function capitalizeFirstLetter(string: string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export const openSteam = () => {
	window.location.href = "steam://openurl/https://store.steampowered.com/app/2139330/Auto_Immune/"
}

export const openDiscord = () => {
	window.location.href = "discord://discordapp.com/invite/BwqaDRfQzh";
}

export function getQueryParams(url: string) {
	console.log(`URL IS: ${url}`);
	const queryParams: any = {};
	// Create a URL object
	const urlObj = new URL(url);
	// Use URLSearchParams to get query parameters
	//@ts-ignore
	for (const [key, value] of urlObj.searchParams) {
		queryParams[key] = value;
	}
	return queryParams;
}

export function waitTimeout(seconds: number) {
	return new Promise(resolve => setTimeout(resolve, seconds * 1000));
}

export function getTierIndex(rank: number) {
	let tier = 7;
	for (let i = RankPerTier.length - 1; i >= 0; i--) {
		if (rank >= RankPerTier[i]) {
			tier = i;
			break;
		}
	}
	return tier;
}

// Three multiplayer wins to get to the next rank
export function getLeagueTier(rank: number) {
	const tier = getTierIndex(rank);
	if (tier > 6) {
		return 0; // Max Tier
	}
	return tier;
}

export function uuidToBase64(uuid: string): string {
	// Remove hyphens from the UUID
	const hexUuid = uuid.replace(/-/g, '');
	// Convert hex to a byte array
	const byteArray = new Uint8Array(hexUuid.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)));
	// Convert byte array to base64 string
	let base64 = btoa(String.fromCharCode(...byteArray));
	// Convert to base64url by replacing '+' with '-', '/' with '_', and removing '='
	base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
	return encodeURIComponent(base64);
}

export function base64ToUUID(base64: string): string {
	// Convert from base64url to base64
	base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
	// Pad with '=' to make the length a multiple of 4
	while (base64.length % 4) {
		base64 += '=';
	}
	// Decode base64 to byte array
	const binaryString = atob(base64);
	const byteArray = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		byteArray[i] = binaryString.charCodeAt(i);
	}
	// Convert byte array to hex string
	const hexArray = Array.from(byteArray, byte => ('0' + byte.toString(16)).slice(-2));
	const hexUuid = hexArray.join('');
	// Insert hyphens to format as UUID
	return `${hexUuid.slice(0, 8)}-${hexUuid.slice(8, 12)}-${hexUuid.slice(12, 16)}-${hexUuid.slice(16, 20)}-${hexUuid.slice(20)}`;
}